import { useState, useEffect } from "react";
import styled from "styled-components";
import { Form, Input, Button, Row, Col, Typography, Card } from "antd";
import { FaFacebookF, FaLinkedinIn, FaTwitter, FaGoogle } from "react-icons/fa";
import BookNowModal from "./BookNowModal";
import { sendContactQuery } from "../../Store/Apis/ReservationApi";
import { toast } from "react-hot-toast";
import {
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  HomeOutlined,
  SendOutlined,
  UserOutlined,
  MessageOutlined
} from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";

const ContactUs = () => {
  const [showBookNowModal, setShowBookNowModal] = useState(false);
  const [isSticky, setSticky] = useState(false);
  const [activeNavItem, setActiveNavItem] = useState('Contact Us');
  const navigate = useNavigate();
  const { Title, Text } = Typography;

  useEffect(() => {
    const handleScroll = () => {
      setSticky(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onFinish = async (values) => {
    const payload = {
      customerName: values.name,
      customerEmail: values.email,
      emailBody: values.query,
    };
  
    try {
      const result = await sendContactQuery(payload);
      console.log("Success:", result);
      
      toast.success("Your message has been sent. We'll get back to you soon!", {duration: 5000});
      navigate("/");
    } catch (error) {
      console.error("Error sending query:", error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const goToHome = () => {
    setActiveNavItem('Home');
    navigate("/");
  };

  const goToContact = () => {
    setActiveNavItem('Contact Us');
    navigate("/contact");
  };

  const goToLogin = () => {
    setActiveNavItem('Staff Sign In');
    navigate("/login");
  };

  const handleBookNow = () => {
    setActiveNavItem('Book Now');
    setShowBookNowModal(true);
  };

  return (
    <PageContainer>
      <StickyNav isSticky={isSticky}>
        <LogoContainer>
          <Logo src="/LogoWaterfront.png" alt="Logo" onClick={goToHome} />
        </LogoContainer>
        <NavItems>
          <NavItem active={activeNavItem === 'Home'} onClick={goToHome}>Home</NavItem>
          <NavItem active={activeNavItem === 'Contact Us'} onClick={goToContact}>Contact Us</NavItem>
          <NavItem active={activeNavItem === 'Staff Sign In'} onClick={goToLogin}>Staff Sign In</NavItem>
          <NavItem active={activeNavItem === 'Book Now'} onClick={handleBookNow}>Book Now</NavItem>
        </NavItems>
      </StickyNav>

      <HeroSection>
        <HeroOverlay />
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle style={{ color: "white" ,fontFamily: "Cormorant Garamond', serif"}}>Get In Touch</HeroTitle>
            <HeroSubtitle>We'd love to hear from you</HeroSubtitle>
          </motion.div>
        </HeroContent>
      </HeroSection>

      <Content>
        <ContactContainer>
          <Row gutter={[40, 40]} justify="center">
            <Col xs={24} sm={24} md={14} lg={12}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <ContactMessageWrapper>
                  <TalkWithTeamText>Talk with our team</TalkWithTeamText>
                  <h2>Any Question? Feel Free to Contact</h2>
                  <p>We would love to hear from you! Whether you have a question about our district, or want to give us feedback, feel free to reach out using the contact details on the right. Our team is here to assist you.</p>
                </ContactMessageWrapper>
                <ContactCard>
                  <CardHeader>
                    <Title level={3}>Send Us a Message</Title>
                    <Text>Fill out the form below and we'll get back to you soon</Text>
                  </CardHeader>
                  
                  <Form
                    layout="vertical"
                    onFinish={onFinish}
                    size="large"
                  >
                    <Form.Item
                      name="name"
                      rules={[{ required: true, message: "Please enter your name" }]}
                    >
                      <StyledInput 
                        prefix={<UserOutlined style={{ color: '#b99028' }} />} 
                        placeholder="Full Name" 
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="email"
                      rules={[
                        {
                          required: true,
                          type: "email",
                          message: "Please enter a valid email",
                        },
                      ]}
                    >
                      <StyledInput 
                        prefix={<MailOutlined style={{ color: '#b99028' }} />} 
                        placeholder="Email Address" 
                      />
                    </Form.Item>
                    
                    <Form.Item 
                      name="query"
                      rules={[{ required: true, message: "Please enter your message" }]}
                    >
                      <StyledTextArea 
                        prefix={<MessageOutlined style={{ color: '#b99028' }} />}
                        rows={5} 
                        placeholder="How can we help you?" 
                      />
                    </Form.Item>
                    
                    <Form.Item>
                      <SubmitButton htmlType="submit">
                        <ButtonText>Send Message</ButtonText>
                        <SendOutlined />
                      </SubmitButton>
                    </Form.Item>
                  </Form>
                </ContactCard>
              </motion.div>
            </Col>
            
            <Col xs={24} sm={24} md={10} lg={8}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <InfoCard>
                  <CardHeader>
                    <Title level={3}>Contact Information</Title>
                    <Text>Reach out to us through any of these channels</Text>
                  </CardHeader>
                  
                  <InfoSection>
                    <InfoItem>
                      <InfoIcon>
                        <PhoneOutlined />
                      </InfoIcon>
                      <InfoContent>
                        <InfoTitle>Phone</InfoTitle>
                        <InfoText>066 520 8802</InfoText>
                      </InfoContent>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoIcon>
                        <MailOutlined />
                      </InfoIcon>
                      <InfoContent>
                        <InfoTitle>Email</InfoTitle>
                        <InfoText>isiqalo.reservations2@gscollege.edu.za</InfoText>
                      </InfoContent>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoIcon>
                        <GlobalOutlined />
                      </InfoIcon>
                      <InfoContent>
                        <InfoTitle>Website</InfoTitle>
                        <InfoText>www.isiqaloguesthouse.co.za</InfoText>
                      </InfoContent>
                    </InfoItem>
                    
                    <InfoItem>
                      <InfoIcon>
                        <HomeOutlined />
                      </InfoIcon>
                      <InfoContent>
                        <InfoTitle>Address</InfoTitle>
                        <InfoText>Portion 9, Plaas Verblyden, Standerton, 2430
</InfoText>
                      </InfoContent>
                    </InfoItem>
                  </InfoSection>
                </InfoCard>
                <SocialContainer>
                      <Title level={3}>Connect With Us</Title>
                    <SocialIcons>
                      <SocialIconLink href="https://www.facebook.com/waterfront247.co.za?mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer">
                        <FaFacebookF />
                      </SocialIconLink>
                      <SocialIconLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                        <FaLinkedinIn />
                      </SocialIconLink>
                      <SocialIconLink href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                        <FaTwitter />
                      </SocialIconLink>
                      <SocialIconLink href="https://google.com" target="_blank" rel="noopener noreferrer">
                        <FaGoogle />
                      </SocialIconLink>
                    </SocialIcons>
                  </SocialContainer>
              </motion.div>
            </Col>
          </Row>
        </ContactContainer>
        
       
      </Content>

      <MapSection>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Title level={3} style={{ textAlign: 'center', marginBottom: '30px' }}>Find Us Here</Title>
          <MapContainer>
            <iframe
              title="Google Maps"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d70693.18114943482!2d29.22207117189658!3d-26.938713617495477!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1eec83116fed00e3%3A0x50c2fd48f3e0b5f!2sWaterfront%20Lodge!5e1!3m2!1sen!2sza!4v1758529813947!5m2!1sen!2sza"  
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: '12px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </MapContainer>
        </motion.div>
      </MapSection>

      {showBookNowModal && (
        <BookNowModal
          visible={showBookNowModal}
          onClose={() => setShowBookNowModal(false)}
        />
      )}

      <FooterSection>
        <FooterContent>
          <LogoSection>
            <Logo1
              src="/footer_logo.png"
              alt="Isiqalo Guesthouse Logo"
              style={{ width: "260px", height: "51px" }}
            />
            <h3
              style={{
                color: "#d2d2d2",
                font: "Open Sans, sans-serif",
                fontSize: "18px",
              }}
            >
              Guesthouse, Self-catering and Bed and Breakfast
            </h3>
          </LogoSection>

          <QuickLinks>
            <h3
              style={{
                color: "white",
                font: "Montserrat, sans-serif",
                fontSize: "19px",
              }}
            >
              Quick Links
            </h3>
            <StyledLink to="/" onClick={goToHome}>
              &gt; Home
            </StyledLink>
            <StyledLink onClick={handleBookNow}>
              &gt; Book Now
            </StyledLink>
            <StyledLink to="/contact" onClick={goToContact}>
              &gt; Contact Us
            </StyledLink>
          </QuickLinks>

          <FooterSocialIcons>
            <h3>Follow Us</h3>
            <div className="icon-container">
              <a
                href="https://www.facebook.com/IsiqaloGuesthouse/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaFacebookF />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaLinkedinIn />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaTwitter />
              </a>
              <a
                href="https://google.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGoogle />
              </a>
            </div>
          </FooterSocialIcons>

          <ContactSection>
            <h3
              style={{
                color: "white",
                font: "Montserrat, sans-serif",
                fontSize: "19px",
              }}
            >
              Contact Us
            </h3>
            <p>5A Kieser Street, Standerton</p>
            <p>+27 17 714 0039</p>
            <p>info@isiqaloguesthouse.co.za</p>
          </ContactSection>
        </FooterContent>
      </FooterSection>

      <CopyrightSection>
        <p>
          Copyright © Isiqalo {new Date().getFullYear()} All rights reserved.
        </p>
      </CopyrightSection>
    </PageContainer>
  );
};

// Styled Components (keep all existing ones)
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const StickyNav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  position: fixed;
  top: 0;
  width: 100%;
  background: rgba(255, 255, 255, ${props => props.isSticky ? '0.95' : '0.85'});
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 50px;
  cursor: pointer;
`;

const NavItems = styled.div`
  display: flex;
  gap: 2rem;
`;

const NavItem = styled.div`
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 500;
  position: relative;
  padding: 0.5rem 0;
  color: ${props => props.active ? '#a17c1f' : '#333'};
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: #a17c1f;
    transition: width 0.3s ease;
  }

  &:hover {
    color: #a17c1f;
    
    &::after {
      width: 100%;
    }
  }
`;

const HeroSection = styled.div`
  height: 40vh;
  background-image: url('/lounge.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 80px;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  letter-spacing: 2px;
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: 1px;
`;

const Content = styled.div`
  flex: 1;
  padding: 0 20px;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: -50px auto 50px;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    margin-top: 20px;
  }
`;

// New styled component for the contact message
const ContactMessageWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
  
  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    margin-bottom: 15px;
    color: #333;
  }
  
  p {
    color: #666;
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const TalkWithTeamText = styled.h3`
  color: #b99028;
  font-size: 1.5rem;
  font-family: 'Satisfy', cursive;
  margin-bottom: 10px;
`;

const ContactCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 40px;
  height: 100%;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const InfoCard = styled(ContactCard)`
  background: #fcfcfc;
`;

const CardHeader = styled.div`
  margin-bottom: 30px;
  
  h3 {
    font-size: 28px;
    margin-bottom: 10px;
    color: #333;
  }
  
  span {
    color: #666;
    font-size: 16px;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 8px;
  height: 50px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  
  &:hover, &:focus {
    border-color: #b99028;
    box-shadow: 0 0 0 2px rgba(185, 144, 40, 0.1);
  }
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  
  &:hover, &:focus {
    border-color: #b99028;
    box-shadow: 0 0 0 2px rgba(185, 144, 40, 0.1);
  }
`;

const SubmitButton = styled(Button)`
  height: 50px;
  border: none;
  border-radius: 8px;
  background: #b99028;
  color: white;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #d4a857;
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(185, 144, 40, 0.3);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const ButtonText = styled.span`
  margin-right: 8px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f5eed7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b99028;
  font-size: 18px;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 5px 0;
`;

const InfoText = styled.p`
  font-size: 15px;
  color: #666;
  margin: 0;
`;

// Social Icons for Info Card
const SocialContainer = styled.div`
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
`;

const SocialTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 56px;
`;

// const SocialIconLink = styled.a`
//   width: 38px;
//   height: 38px;
//   border-radius: 50%;
//   background: #f5eed7;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   color: #b99028;
//   transition: all 0.3s ease;
  
//   &:hover {
//     background: #b99028;
//     color: white;
//     transform: translateY(-3px);
//   }
// `;


const SocialIconLink = styled.a`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #f5eed7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b99028;
  font-size: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #b99028;
    color: white;
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(185, 144, 40, 0.3);
  }
`;

const MapSection = styled.div`
  padding: 0 20px 60px;
  max-width: 1200px;
  margin: 0 auto;
`;

const MapContainer = styled.div`
  height: 400px;
  width: 95vw;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  position: relative;
  left: 50%;
  transform: translateX(-50%);
`;

const FooterSection = styled.section`
  background-color: black;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 40px 20px;
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  width: 80%;
  gap: 20px;
  flex-wrap: wrap;
`;

const LogoSection = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const QuickLinks = styled.div`
  flex: 1;
  min-width: 200px;
  text-align: center;
`;

const FooterSocialIcons = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 10px;

  h3 {
    color: white;
    font-size: 18px;
    font-family: "Montserrat, sans-serif";
    margin: 0;
  }

  .icon-container {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  a {
    color: white;
    font-size: 18px;
    transition: color 0.3s ease-in-out;

    &:hover {
      color: #f39c12;
    }
  }
`;

const ContactSection = styled.div`
  flex: 1;
  min-width: 200px;
  text-align: center;
`;

const CopyrightSection = styled.div`
  background-color: black;
  border-top: 1px solid #444;
  height: 10vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const StyledLink = styled(Link)`
  display: block;
  color: white;
  font-size: 18px;
  text-decoration: none;
  margin: 5px 0;
  transition: color 0.3s ease-in-out;

  &:hover {
    color: #f39c12;
  }
`;

const Logo1 = styled.img`
  width: 180px;
  margin-bottom: 10px;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

export default ContactUs;